import { ReactElement, ReactNode } from "react";
import { Link } from "react-router-dom";
import type {
  ImportReviewLists,
  ReviewIssueRow,
  ReviewNamedRow,
  ReviewPersonRow,
} from "./resultSummaryModel";
import ProfessionalAdditions from "./ProfessionalAdditions";
import {
  PANEL,
  SECTION_TITLE,
  TABLE_CELL,
  TABLE_HEAD,
  TABLE_ROW,
} from "./staffSurface";

interface ReviewListsProps {
  lists: ImportReviewLists;
  firmId: string;
}

export default function ReviewLists({ lists, firmId }: ReviewListsProps): ReactElement {
  return (
    <section className={PANEL}>
      <h2 className={SECTION_TITLE}>Needs review</h2>
      <div className="flex flex-col gap-3">
        <ReviewSection
          title="Professionals additions"
          count={lists.additions.length}
          hint="The import creates no login. Select professionals to create their B2C accounts and email their credentials."
        >
          <ProfessionalAdditions firmId={firmId} rows={lists.additions} />
        </ReviewSection>

        <ReviewSection title="Professionals updates" count={lists.updates.length}>
          <PersonTable
            rows={lists.updates}
            empty="No professionals were updated."
          />
        </ReviewSection>

        <ReviewSection
          title="Professionals deletions"
          count={lists.deletions.length}
          hint="Professionals missing from the feed. Nothing was deleted."
        >
          <PersonTable
            rows={lists.deletions}
            empty="No professionals were flagged for deletion."
          />
        </ReviewSection>

        <ReviewSection
          title="Practice areas to examine"
          count={lists.practiceAreas.length}
          hint="New catalogue entries land unapproved."
        >
          <NameTable
            rows={lists.practiceAreas}
            empty="No practice areas were added."
          />
        </ReviewSection>
        <ReviewSection
          title="Not parsed"
          count={lists.mappingIssues.length}
        >
          <IssueTable
            rows={lists.mappingIssues}
            empty="Every professional in the feed parsed."
          />
        </ReviewSection>
        <ReviewSection
          title="Write failures"
          count={lists.writeFailures.length}
        >
          <IssueTable
            rows={lists.writeFailures}
            empty="Every write succeeded."
          />
        </ReviewSection>
      </div>
    </section>
  );
}

function ReviewSection({
  title,
  count,
  hint,
  children,
}: {
  title: string;
  count: number;
  hint?: string;
  children: ReactNode;
}): ReactElement {
  return (
    <details
      className="rounded border border-gray-200 bg-white"
      open={count > 0}
    >
      <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-gray-font font-decimal-medium">
        {title}
        <span className="ml-2 font-normal text-gray-500">({count})</span>
      </summary>
      <div className="border-t border-gray-100 px-4 py-3">
        {hint && <p className="mb-3 text-xs text-gray-500">{hint}</p>}
        {children}
      </div>
    </details>
  );
}

function professionalName(row: ReviewPersonRow): ReactNode {
  const label = row.name || "—";
  if (!row.personId) {
    return label;
  }
  return (
    <Link
      to={`/staff/profile/attorney/${row.personId}`}
      className="text-secondary"
    >
      {label}
    </Link>
  );
}

function PersonTable({
  rows,
  empty,
}: {
  rows: ReviewPersonRow[];
  empty: string;
}): ReactElement {
  return (
    <ScrollTable>
      <thead>
        <tr>
          <th className={TABLE_HEAD}>Professional</th>
          <th className={TABLE_HEAD}>Email</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <EmptyRow colSpan={2} text={empty} />
        ) : (
          rows.map((row, index) => (
            <tr key={`${row.email}-${row.name}-${index}`} className={TABLE_ROW}>
              <td className={TABLE_CELL}>{professionalName(row)}</td>
              <td className={TABLE_CELL}>{row.email || "—"}</td>
            </tr>
          ))
        )}
      </tbody>
    </ScrollTable>
  );
}

function NameTable({
  rows,
  empty,
}: {
  rows: ReviewNamedRow[];
  empty: string;
}): ReactElement {
  return (
    <ScrollTable>
      <thead>
        <tr>
          <th className={TABLE_HEAD}>Practice area</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <EmptyRow colSpan={1} text={empty} />
        ) : (
          rows.map((row, index) => (
            <tr key={`${row.name}-${index}`} className={TABLE_ROW}>
              <td className={TABLE_CELL}>{row.name || "—"}</td>
            </tr>
          ))
        )}
      </tbody>
    </ScrollTable>
  );
}

function IssueTable({
  rows,
  empty,
}: {
  rows: ReviewIssueRow[];
  empty: string;
}): ReactElement {
  return (
    <ScrollTable>
      <thead>
        <tr>
          <th className={TABLE_HEAD}>Item</th>
          <th className={TABLE_HEAD}>Reason</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <EmptyRow colSpan={2} text={empty} />
        ) : (
          rows.map((row, index) => (
            <tr key={`${row.item}-${index}`} className={TABLE_ROW}>
              <td className={TABLE_CELL}>{row.item}</td>
              <td className={TABLE_CELL}>{row.reason || "—"}</td>
            </tr>
          ))
        )}
      </tbody>
    </ScrollTable>
  );
}

function ScrollTable({ children }: { children: ReactNode }): ReactElement {
  return (
    <div className="max-h-80 overflow-auto">
      <table className="w-full border-collapse text-sm text-gray-font">
        {children}
      </table>
    </div>
  );
}

function EmptyRow({
  colSpan,
  text,
}: {
  colSpan: number;
  text: string;
}): ReactElement {
  return (
    <tr>
      <td colSpan={colSpan} className={`${TABLE_CELL} italic text-gray-500`}>
        {text}
      </td>
    </tr>
  );
}
